import React, { useEffect, useRef } from "react";
import type { Preset } from "./presets.js";

export type RenderWhere = "browser" | "server";

/**
 * Where to render, asked rather than decided.
 *
 * The two paths are genuinely different bargains and only the person exporting
 * can weigh them. Their own machine is faster and free — it has a GPU and our
 * workers do not, so every frame there is rasterised in software — but it holds
 * the tab hostage for the duration. Our servers cost money and take longer, and
 * they can walk away.
 *
 * Deciding for them was the old behaviour and it was defensible while both
 * were free. It stopped being defensible when one of them started costing
 * money: a silent fallback that charges somebody is a bill they did not agree
 * to.
 *
 * Shown on the export button rather than beside the preset list, because that
 * is the moment the question is live — before then it is a hypothetical about
 * a video they may not export.
 */
export const ExportChoice: React.FC<{
  open: boolean;
  preset: Preset;
  /** Null while the encoder is still being asked. */
  canRenderHere: boolean | null;
  /** Why it cannot, when it cannot. Empty otherwise. */
  reasons: string[];
  serverPriceUsd: number;
  formatUsd: (amount: number) => string;
  onChoose: (where: RenderWhere) => void;
  onCancel: () => void;
}> = ({ open, preset, canRenderHere, reasons, serverPriceUsd, formatUsd, onChoose, onCancel }) => {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  // Every size that can be picked can be made on a server. The exception was
  // 4K, which is not offered at all now — so this dialog no longer has a
  // branch for a preset only the browser could handle.
  const browserAvailable = canRenderHere === true;

  return (
    <dialog
      ref={ref}
      onCancel={(event) => {
        event.preventDefault();
        onCancel();
      }}
      onClose={() => {
        if (open) onCancel();
      }}
      // `m-auto` restores the centring the UA stylesheet gives a modal dialog;
      // Tailwind's preflight resets margin to 0 and parks it in the corner.
      className="m-auto w-[460px] max-w-[92vw] rounded-[14px] border border-[var(--edge-strong)] bg-bay-850 p-0 text-ink-100 shadow-2xl backdrop:bg-black/60 backdrop:backdrop-blur-[2px]"
    >
      <div className="flex flex-col gap-3 p-5">
        <div>
          <h2 className="text-[15px] font-semibold text-ink-100">{"Where should this render?"}</h2>
          <p className="mt-1 text-[12px] text-ink-400">{preset.label}</p>
        </div>

        <div className="flex flex-col gap-2">
          <Option
            available={browserAvailable}
            pending={canRenderHere === null}
            title="In this browser"
            price="Free"
            body={
              canRenderHere === null
                ? "Checking whether your browser can encode this…"
                : browserAvailable
                  ? "Your machine does the work, which is faster than our servers. Keep this tab open and in front until it finishes."
                  : (reasons[0] ?? "This browser cannot encode that video.")
            }
            onChoose={() => onChoose("browser")}
          />

          <Option
            available
            pending={false}
            title="On our servers"
            price={serverPriceUsd > 0 ? formatUsd(serverPriceUsd) : "Free"}
            body="Slower, but you can close the tab and come back to it."
            onChoose={() => onChoose("server")}
          />
        </div>

        <div className="mt-1 flex justify-end">
          <button type="button" className="btn btn-ghost" onClick={onCancel}>
            {"Cancel"}
          </button>
        </div>
      </div>
    </dialog>
  );
};

/**
 * One of the two, as a button rather than a radio.
 *
 * A radio plus a confirm is two decisions where there is one. The card is the
 * action, so choosing it starts the export.
 */
const Option: React.FC<{
  available: boolean;
  pending: boolean;
  title: string;
  price: string;
  body: string;
  onChoose: () => void;
}> = ({ available, pending, title, price, body, onChoose }) => (
  <button
    type="button"
    disabled={!available || pending}
    onClick={onChoose}
    className="rounded-[10px] border border-[var(--edge)] bg-bay-900 p-3 text-left transition-colors enabled:hover:border-ember-500 disabled:opacity-55"
  >
    <span className="flex items-baseline justify-between gap-3">
      <span className="text-[13px] font-semibold text-ink-100">{title}</span>
      <span className="shrink-0 text-[12.5px] tabular-nums text-ink-200">{price}</span>
    </span>
    <span className="mt-1 block text-[11.5px] leading-relaxed text-ink-400">{body}</span>
  </button>
);
